import { FC, useEffect, useMemo, useRef, useState } from "react";
import { FieldValues, useFormContext, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetClientSalesOfficesList,
  useGetPurchaseOrders,
} from "../../../../api/services/client/clientApis";
import { DropDownData } from "../../../../api/services/common/models";
import { useGetDeliveryOfficeListQuery } from "../../../../api/services/mdmOffice/mdmOfficeApis";
import {
  useGetLineOfBusiness,
  useGetProgramFrameWork,
} from "../../../../api/services/programTemplate/programTemplateApis";
import { useGetInvoiceHistoryByProjectId } from "../../../../api/services/project/projectApis";
import {
  AccordionHeader,
  AccordionHeaderText,
  AutoCompleteOption,
  FormController,
  MuiGrid,
  MuiMultiSelect,
  MuiSelect,
  MuiTextField,
  OptionsDataType,
  STATUS_BADGE_TYPE,
} from "../../../../components";
import { ClientAutoComplete } from "../../../../components/clientAutoComplete/ClientAutoComplete";
import {
  CLIENT_TYPE_NON_CLIENT_BILLING_PARTY_SITE_CORE_KEY_VALUE,
  EMPTY_VALUE_DASH,
  LANGUAGE_CODE,
  MAX_LENGTH_75,
  MDM_ACTIVITY_TYPE_KEY,
} from "../../../../helpers/constants";
import {
  getValueOrBlank,
  getValueOrDash,
  isEmpty,
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useMultiSelectARIALabels } from "../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import {
  keyDetailsSelector,
  projectOpcoFieldEditableSelector,
  projectStatusSelector,
} from "../../../../redux/slices/project/projectSelector";
import { CreateProjectFormKey } from "../../modelsAndSchemas/Models";
import { keyProjectDetailsSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { CreateProjectKeyInputWrapper } from "./CreateProjectKeyInput.style";
interface CreateProjectKeyInputProps extends FieldValues {
  pageId: string;
  isUpdateDetail?: boolean;
  productLinesDropdownRef?: React.RefObject<{
    resetValues: (values?: string[]) => void;
  }>;
  poNumberDropdownRef?: React.RefObject<{
    resetValues: (values?: string[]) => void;
  }>;
}

export const CreateProjectKeyInput: FC<CreateProjectKeyInputProps> = ({
  pageId,
  isUpdateDetail = false,
  poNumberDropdownRef,
  productLinesDropdownRef,
}) => {
  const {
    control,
    setValue,
    getValues,
    setError,
    formState: { dirtyFields },
  } = useFormContext();

  const projectKeyDetails = useSelector(keyDetailsSelector);
  const projectOpcoFieldEditable = useSelector(
    projectOpcoFieldEditableSelector,
  );
  const [primaryProductLineOptions, setPrimaryProductLineOptions] = useState<
    OptionsDataType[]
  >([]);
  const [
    allowModifyPrimaryDeliveryOffice,
    setAllowModifyPrimaryDeliveryOffice,
  ] = useState<boolean>(true);
  const isPrimaryProductLineChangedRef = useRef<boolean>(false);
  const [selectedClientAutoCompleteValue, setSelectedClientAutoCompleteValue] =
    useState<AutoCompleteOption | null>(null);

  const {
    commonLabels,
    fetchLabel,
    fetchComponentTitle,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const dispatch = useDispatch();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(isUpdateDetail);

  const {
    data: purchaseOrdersList,
    refetchWithParams: refetchPurchaseOrders,
    isFetching: purchaseOrderPending,
    error: purchaseOrdersError,
  } = useGetPurchaseOrders();

  const {
    data: lineOfBusinesses,
    isFetching: businessPending,
    isError: lobError,
  } = useGetLineOfBusiness(LANGUAGE_CODE.EN_US);

  const {
    data: projectFrameworksResponse,
    isFetching: projectFrameworksPending,
    isError: projectFrameworksError,
  } = useGetProgramFrameWork(
    LANGUAGE_CODE.EN_US,
    MDM_ACTIVITY_TYPE_KEY.project,
  );

  const {
    data: deliveryOfficeListData,
    isLoading: deliveryOfficeListPending,
    isError: isDeliveryOfficeListError,
  } = useGetDeliveryOfficeListQuery();

  const {
    data: projectInvoiceHistory,
    refetchWithParams: refetchProjectInvoiceHistory,
    isPending: isProjectInvoiceHistoryFetching,
  } = useGetInvoiceHistoryByProjectId();

  const {
    data: clientSalesOfficeData,
    refetchWithParams: refetchClientSalesOfficeData,
    isFetching: isClientSalesOfficeFetching,
    isSuccess: isClientSalesOfficeSuccess,
  } = useGetClientSalesOfficesList();

  useEffect(() => {
    if (isUpdateDetail && projectKeyDetails?.id) {
      refetchProjectInvoiceHistory(projectKeyDetails?.id);
    }
  }, [isUpdateDetail, projectKeyDetails]);

  useEffect(() => {
    if (projectInvoiceHistory && isNotEmpty(projectInvoiceHistory)) {
      const invoicesList = projectInvoiceHistory.invoices;
      // check if Project has already been invoiced
      setAllowModifyPrimaryDeliveryOffice(isEmpty(invoicesList));
    }
  }, [projectInvoiceHistory]);

  const clientId = useWatch({
    name: CreateProjectFormKey.client,
    control,
  });

  const salesOfficeId = useWatch({
    name: CreateProjectFormKey.salesOfficeId,
    control,
  });

  const removeValueFromClientDependentFields = () => {
    setValue(CreateProjectFormKey.poNumbers, []);
    poNumberDropdownRef?.current?.resetValues([]);
  };

  const formValues = useWatch({ control });
  const productLinesFormValues = useWatch({
    name: CreateProjectFormKey.productLines,
    control,
  });

  const projectStatus = useSelector(projectStatusSelector);

  useEffect(() => {
    const productLineIds = formValues?.productLines;
    const poNumberIds = formValues?.poNumbers;

    if (isNotEmpty(productLineIds) && productLinesDropdownRef?.current) {
      productLinesDropdownRef.current.resetValues(productLineIds);
    }
    if (isNotEmpty(poNumberIds) && poNumberDropdownRef?.current) {
      poNumberDropdownRef.current.resetValues(poNumberIds);
    }
  }, [formValues]);

  useEffect(() => {
    if (isEmpty(lineOfBusinesses)) return;

    const selectedProductLines: DropDownData[] = (
      lineOfBusinesses || []
    ).filter((line: DropDownData) => productLinesFormValues?.includes(line.id));

    setPrimaryProductLineOptions(
      setApiDataToDropDownOption(selectedProductLines),
    );

    const primaryProductLineSelectedId = getValues(
      CreateProjectFormKey.primaryProductLine,
    );

    // Case 1: No Product-lines selected
    if (isEmpty(selectedProductLines)) {
      if (isPrimaryProductLineChangedRef.current) {
        setValue(CreateProjectFormKey.primaryProductLine, "", {
          shouldValidate: true,
        });
      }
      return;
    }

    // Case 2: Some or Few Product-lines are selected
    const isPrimaryProductLineExists = selectedProductLines.some(
      (line) => line.id === primaryProductLineSelectedId,
    );

    if (
      !isPrimaryProductLineExists &&
      isNotEmpty(primaryProductLineSelectedId)
    ) {
      setValue(CreateProjectFormKey.primaryProductLine, "");
    }
  }, [productLinesFormValues]);

  useEffect(() => {
    if ((isNotEmpty(clientId) && dirtyFields?.client) || isUpdateDetail) {
      refetchPurchaseOrders({
        clientCode: clientId,
        statusId: STATUS_BADGE_TYPE.active,
      });

      !isUpdateDetail && removeValueFromClientDependentFields();
    }
  }, [clientId, dirtyFields?.client, isUpdateDetail]);

  useEffect(() => {
    if (isNotEmpty(clientId) && dirtyFields?.client) {
      setValue(CreateProjectFormKey.salesOfficeId, "");
      callSalesOfficeApi(clientId);

      !isUpdateDetail && removeValueFromClientDependentFields();
    }
  }, [clientId, dirtyFields?.client, isUpdateDetail]);

  useEffect(() => {
    if (projectKeyDetails?.client?.id && isUpdateDetail) {
      callSalesOfficeApi(projectKeyDetails?.client?.id);
    }
  }, [isUpdateDetail, projectKeyDetails?.client?.id]);

  const callSalesOfficeApi = (clientId: string) => {
    refetchClientSalesOfficeData(clientId);
  };

  useEffect(() => {
    if (isNotEmpty(salesOfficeId) && clientSalesOfficeData) {
      const selectedSalesOfficeId = clientSalesOfficeData?.find(
        (office) => office.id === salesOfficeId,
      );
      setValue(
        CreateProjectFormKey.billingOffice,
        selectedSalesOfficeId?.billingOffice?.name,
      );
    }
  }, [salesOfficeId, clientSalesOfficeData]);

  useEffect(() => {
    if (isClientSalesOfficeSuccess && clientSalesOfficeData) {
      if (!isUpdateDetail) {
        // don't set sales office in Update to avoid overwriting from Client again
        const selectedSalesOfficeId = clientSalesOfficeData.find(
          (office) => office.isSelected,
        );
        setValue(CreateProjectFormKey.salesOfficeId, selectedSalesOfficeId?.id);
      }
    }
  }, [isClientSalesOfficeSuccess, clientSalesOfficeData, isUpdateDetail]);

  const poDropdownValue = useMemo(() => {
    if (isEmpty(purchaseOrdersList) || isEmpty(clientId)) {
      return [];
    }
    return purchaseOrdersList!.map((po) => ({
      label: po.poNumber,
      value: po.purchaseOrderId,
    }));
  }, [purchaseOrdersList]);

  const projectFrameworksDropdownValue = useMemo(() => {
    if (isEmpty(projectFrameworksResponse)) {
      return [];
    }
    return setApiDataToDropDownOption(projectFrameworksResponse);
  }, [projectFrameworksResponse]);

  const isLoading =
    businessPending ||
    purchaseOrderPending ||
    projectFrameworksPending ||
    deliveryOfficeListPending ||
    isProjectInvoiceHistoryFetching ||
    isClientSalesOfficeFetching;

  const isError =
    lobError ||
    purchaseOrdersError ||
    projectFrameworksError ||
    isDeliveryOfficeListError;
  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isLoading,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isLoading]);

  useEffect(() => {
    if (isError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [isError]);

  const shouldPreventOnChangeError = (
    selectedId: string,
    existingId?: string,
  ): boolean => {
    return (
      isNotEmpty(selectedId) &&
      isUpdateDetail &&
      !allowModifyPrimaryDeliveryOffice &&
      selectedId !== existingId
    );
  };

  const displayPrimaryDeliveryOfficeError = () => {
    setTimeout(() => {
      setError(CreateProjectFormKey.primaryDeliveryOffice, {
        type: "custom",
        message: getLabel(
          keyProjectDetailsSiteCoreKeys.primaryDeliveryOfficeChangeErrorMessage,
        ),
      });
    }, 0);
  };

  const handlePrimaryDeliveryOfficeBlur = (primaryDeliveryOfficeId: string) => {
    if (
      shouldPreventOnChangeError(
        primaryDeliveryOfficeId,
        projectKeyDetails?.primaryDeliveryOffice?.id,
      )
    ) {
      displayPrimaryDeliveryOfficeError();
    }
  };

  const handlePrimaryDeliveryOfficeChange = (
    primaryDeliveryOfficeId: string,
  ) => {
    if (isEmpty(primaryDeliveryOfficeId)) {
      setValue(CreateProjectFormKey.primaryDeliveryCountry, EMPTY_VALUE_DASH);
      return;
    }

    if (
      shouldPreventOnChangeError(
        primaryDeliveryOfficeId,
        projectKeyDetails?.primaryDeliveryOffice?.id,
      )
    ) {
      displayPrimaryDeliveryOfficeError();
      return;
    }

    const selectedPrimaryDeliveryOffice = deliveryOfficeListData?.find(
      (office) => office.id === primaryDeliveryOfficeId,
    );
    setValue(
      CreateProjectFormKey.primaryDeliveryCountry,
      getValueOrDash(selectedPrimaryDeliveryOffice?.countryName),
    );
  };

  const handlePrimaryProductLineChangeError = (
    primaryProductLineId: string,
  ) => {
    if (!isUpdateDetail && primaryProductLineId) {
      isPrimaryProductLineChangedRef.current = true;
    }
    if (
      shouldPreventOnChangeError(
        primaryProductLineId,
        projectKeyDetails?.primaryProductLine?.id,
      )
    ) {
      setTimeout(() => {
        setError(CreateProjectFormKey.primaryProductLine, {
          type: "custom",
          message: getLabel(
            keyProjectDetailsSiteCoreKeys.primaryProductLineChangeErrorMessage,
          ),
        });
      }, 0);
    }
  };

  const getLabel = (key: string) => {
    return fetchLabel(keyProjectDetailsSiteCoreKeys.keyProjectDetails, key);
  };

  const renderHeader = (label: string) => {
    return <AccordionHeaderText label={label} />;
  };

  const getSelectedActualClientAutoCompleteValue = () => {
    if (isUpdateDetail) {
      return {
        selectedValue: {
          id: getValueOrBlank(projectKeyDetails?.actualClient?.id),
          label: getValueOrBlank(projectKeyDetails?.actualClient?.name),
          labelTwo: getValueOrBlank(projectKeyDetails?.actualClientCode),
        },
      };
    }
  };

  const isClientTypeNCBP = () => {
    const clientType = isUpdateDetail
      ? projectKeyDetails?.clientType
      : selectedClientAutoCompleteValue?.clientType;

    return (
      clientType === CLIENT_TYPE_NON_CLIENT_BILLING_PARTY_SITE_CORE_KEY_VALUE
    );
  };

  const onChangeActualClientAutoComplete = (value: AutoCompleteOption) => {
    setSelectedClientAutoCompleteValue(value);
  };

  return (
    <CreateProjectKeyInputWrapper>
      <AccordionHeader
        title={renderHeader(
          fetchComponentTitle(keyProjectDetailsSiteCoreKeys.keyProjectDetails),
        )}
        isExpanded={true}
        className={`${isUpdateDetail && "update-input-general"} `}
        showExpandCollapseIcon={true}
      >
        <form data-testid="create-project-key-input">
          <MuiGrid container className="form-container">
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateProjectFormKey.projectName}
              >
                <MuiTextField
                  label={getLabel(keyProjectDetailsSiteCoreKeys.projectName)}
                  placeholder={commonLabels.inputPlaceHolder}
                  id={`${pageId}-${CreateProjectFormKey.projectName}`}
                  requiredARIALabel={commonARIALabel.required}
                  isMandatory
                  maxLength={MAX_LENGTH_75}
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <ClientAutoComplete
                isMandatory
                label={getLabel(keyProjectDetailsSiteCoreKeys.client)}
                formId={CreateProjectFormKey.client}
                control={control}
                disabled={isUpdateDetail}
                isActiveClientOnly
                onChangeValue={onChangeActualClientAutoComplete}
                {...(isUpdateDetail && {
                  selectedValue: {
                    id: getValueOrBlank(projectKeyDetails?.client?.id),
                    label: getValueOrBlank(projectKeyDetails?.client?.name),
                    labelTwo: "",
                  },
                })}
              />
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateProjectFormKey.clientBusinessUnit}
              >
                <MuiTextField
                  label={getLabel(
                    keyProjectDetailsSiteCoreKeys.clientBusinessUnit,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  id={`${pageId}-${CreateProjectFormKey.clientBusinessUnit}`}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateProjectFormKey.clientCostCenter}
              >
                <MuiTextField
                  label={getLabel(
                    keyProjectDetailsSiteCoreKeys.clientCostCenter,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  id={`${pageId}-${CreateProjectFormKey.clientCostCenter}`}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateProjectFormKey.primaryDeliveryOffice}
              >
                <MuiSelect
                  label={getLabel(
                    keyProjectDetailsSiteCoreKeys.primaryDeliveryOffice,
                  )}
                  options={setApiDataToDropDownOption(deliveryOfficeListData)}
                  onChange={(event) =>
                    handlePrimaryDeliveryOfficeChange(
                      event.target.value as string,
                    )
                  }
                  onBlur={(event) =>
                    handlePrimaryDeliveryOfficeBlur(event.target.value)
                  }
                  visibleDefaultSelectOption
                  id={`${pageId}-${CreateProjectFormKey.primaryDeliveryOffice}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  isMandatory
                  requiredARIALabel={commonARIALabel.required}
                  showSearch
                  searchPlaceholder={commonLabels.search}
                  disabled={projectStatus === STATUS_BADGE_TYPE.closed}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateProjectFormKey.primaryDeliveryCountry}
              >
                <MuiTextField
                  label={getLabel(
                    keyProjectDetailsSiteCoreKeys.primaryDeliveryCountry,
                  )}
                  id={`${pageId}-${CreateProjectFormKey.primaryDeliveryCountry}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                  disabled={true}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateProjectFormKey.productLines}
                ref={productLinesDropdownRef}
              >
                <MuiMultiSelect
                  label={getLabel(
                    keyProjectDetailsSiteCoreKeys.productLineDropDown,
                  )}
                  options={setApiDataToDropDownOption(lineOfBusinesses)}
                  showSearch
                  showSelectAllOption
                  value={""}
                  showChip
                  chipId={`${pageId}-${CreateProjectFormKey.productLines}-chip`}
                  dialogTitle={getLabel(
                    keyProjectDetailsSiteCoreKeys.productLineDropDown,
                  )}
                  id={`${pageId}-${CreateProjectFormKey.productLines}`}
                  selectPlaceHolder={commonLabels.selectPlaceHolder}
                  searchPlaceHolder={commonLabels.search}
                  selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                  doneText={commonLabels.done}
                  isMandatory
                  allARIAProps={multiSelectARIALabels}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateProjectFormKey.primaryProductLine}
              >
                <MuiSelect
                  label={getLabel(
                    keyProjectDetailsSiteCoreKeys.primaryProductLineDropDown,
                  )}
                  options={primaryProductLineOptions}
                  onChange={(event) =>
                    handlePrimaryProductLineChangeError(
                      event.target.value as string,
                    )
                  }
                  onBlur={(event) =>
                    handlePrimaryProductLineChangeError(event.target.value)
                  }
                  visibleDefaultSelectOption
                  id={`${pageId}-${CreateProjectFormKey.primaryProductLine}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  isMandatory
                  requiredARIALabel={commonARIALabel.required}
                  showSearch
                  searchPlaceholder={commonLabels.search}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateProjectFormKey.poNumbers}
                ref={poNumberDropdownRef}
              >
                <MuiMultiSelect
                  label={getLabel(
                    keyProjectDetailsSiteCoreKeys.poNumberDropdown,
                  )}
                  options={poDropdownValue}
                  showSearch
                  showChip
                  showSelectAllOption
                  chipId={`${pageId}-${CreateProjectFormKey.poNumbers}-chip`}
                  dialogTitle={getLabel(
                    keyProjectDetailsSiteCoreKeys.poNumberDropdown,
                  )}
                  id={`${pageId}-${CreateProjectFormKey.poNumbers}`}
                  selectPlaceHolder={commonLabels.selectPlaceHolder}
                  searchPlaceHolder={commonLabels.search}
                  selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                  doneText={commonLabels.done}
                  allARIAProps={multiSelectARIALabels}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateProjectFormKey.projectFramework}
              >
                <MuiSelect
                  label={getLabel(
                    keyProjectDetailsSiteCoreKeys.frameworkDropDown,
                  )}
                  options={projectFrameworksDropdownValue}
                  visibleDefaultSelectOption
                  showSearch
                  id={`${pageId}-${CreateProjectFormKey.projectFramework}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  isMandatory
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateProjectFormKey.salesOfficeId}
              >
                <MuiSelect
                  isMandatory
                  id={`${pageId}-${CreateProjectFormKey.salesOfficeId}`}
                  label={getLabel(keyProjectDetailsSiteCoreKeys.salesOffice)}
                  visibleDefaultSelectOption
                  showSearch
                  options={setApiDataToDropDownOption(clientSalesOfficeData)}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                  disabled={isUpdateDetail}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateProjectFormKey.billingOffice}
              >
                <MuiTextField
                  label={getLabel(keyProjectDetailsSiteCoreKeys.billingOffice)}
                  placeholder={commonLabels.inputPlaceHolder}
                  id={`${pageId}-${CreateProjectFormKey.billingOffice}`}
                  disabled
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateProjectFormKey.dossierNumber}
              >
                <MuiTextField
                  id={`${pageId}-${CreateProjectFormKey.dossierNumber}`}
                  label={getLabel(keyProjectDetailsSiteCoreKeys.dossierNumber)}
                  placeholder={commonLabels.inputPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                  maxLength={MAX_LENGTH_75}
                  disabled={isUpdateDetail && !projectOpcoFieldEditable}
                />
              </FormController>
            </MuiGrid>
            {isNotEmpty(clientId) && isClientTypeNCBP() && (
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <FormController
                  control={control}
                  name={CreateProjectFormKey.actualClientId}
                >
                  <ClientAutoComplete
                    label={getLabel(keyProjectDetailsSiteCoreKeys.actualClient)}
                    formId={CreateProjectFormKey.actualClientId}
                    control={control}
                    isActiveClientOnly
                    excludeNCBPClient
                    disabled={isUpdateDetail && !projectOpcoFieldEditable}
                    {...getSelectedActualClientAutoCompleteValue()}
                  />
                </FormController>
              </MuiGrid>
            )}
          </MuiGrid>
        </form>
      </AccordionHeader>
    </CreateProjectKeyInputWrapper>
  );
};
